import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { EditDialog, type FieldDef } from "@/components/resource-page";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errorMessage, ifMatch } from "@/services/api";
import { clubOptions, userOptions } from "@/utils/geo";
import type { Translate, TranslationKey } from "@/constants/i18n";
import { useCan } from "@/utils/permissions";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/**
 * The records that hang off a member, shown only in the member's detail view:
 * the Details tabs, each a table with Add, Edit, Delete over the nested
 * `/members/{id}/…` routes. The member is implied, so no form asks for one.
 *
 * The system writes most of these rows itself — an enrolment queues the print
 * job, an amendment files its own line — and a person adds or corrects one by
 * hand when the record and the world disagree. Each verb is behind its own
 * permission (`add__member_card_history`), so a tab is read-only to anyone
 * not granted the write.
 */

export type Row = { id: number; updated_at?: string } & Record<string, unknown>;

export type HistoryKey =
  | "card"
  | "acquisition"
  | "print"
  | "transactions"
  | "amendments"
  | "supplementary"
  | "attachments"
  | "notes";

interface Section {
  key: HistoryKey;
  label: string;
  path: string;
  singular: string;
  fields: FieldDef[];
  /** What the table shows, in order; the rest is in the form. */
  columns: { key: string; label: string }[];
  /** The permission model the verbs are scoped to: `member_card_history`. */
  model: string;
}

export const sectionsOf = (t: Translate): Section[] => {
  const opt = (pairs: [string, TranslationKey][]) =>
    pairs.map(([value, label]) => ({ value, label: t(label) }));
  const col = (key: string, label: TranslationKey) => ({ key, label: t(label) });
  return [
    {
      key: "card",
      model: "member_card_history",
      label: t("hist.cardHistory"),
      path: "card-history",
      singular: t("hist.cardHistory"),
      columns: [
        col("card_no_from", "hist.cardNoFrom"),
        col("card_no_to", "hist.cardNumberTo"),
        col("description", "hist.description"),
        col("amendment_at", "hist.amendmentDateTime"),
        col("created_by_id", "hist.createdBy"),
        col("number_of_years", "hist.noOfYears"),
      ],
      fields: [
        { name: "card_no_from", label: t("hist.cardNoFrom") },
        { name: "card_no_to", label: t("hist.cardNoTo") },
        { name: "description", label: t("hist.description"), type: "textarea" },
        { name: "amendment_at", label: t("hist.amendmentDateTime"), type: "date" },
        {
          name: "number_of_years",
          label: t("hist.noOfYears"),
          type: "number",
          step: "0.5",
          min: 0,
        },
      ],
    },
    {
      key: "acquisition",
      model: "member_acquisition_detail",
      label: t("hist.acquisitionDetails"),
      path: "acquisition-details",
      singular: t("hist.acquisitionDetail"),
      columns: [
        col("transaction_no", "hist.transactionNo"),
        col("payment_purpose", "hist.paymentPurpose"),
        col("amount", "hist.amount"),
        col("payment_type", "hist.paymentType"),
        col("transaction_date", "hist.transactionDate"),
        col("operator_number", "hist.operatorNumber"),
        col("created_by_id", "hist.createdBy"),
      ],
      fields: [
        { name: "transaction_no", label: t("hist.transactionNo") },
        { name: "payment_purpose", label: t("hist.paymentPurpose") },
        { name: "amount", label: t("hist.amount"), type: "number", step: "0.01", min: 0 },
        { name: "payment_type", label: t("hist.paymentType") },
        { name: "transaction_date", label: t("hist.transactionDate"), type: "date" },
        { name: "operator_number", label: t("hist.operatorNumber") },
      ],
    },
    {
      key: "print",
      model: "member_print_history",
      label: t("hist.printingHistory"),
      path: "print-history",
      singular: t("hist.print"),
      columns: [
        col("print_type", "hist.printType"),
        col("print_reason", "hist.printReason"),
        col("print_status", "hist.printStatus"),
        col("date_printed", "hist.datePrinted"),
        col("card_number", "hist.cardNumber"),
        col("additional_reason", "hist.additionalReason"),
        col("created_by_id", "hist.createdBy"),
      ],
      fields: [
        {
          name: "print_type",
          label: t("hist.printType"),
          type: "select",
          options: opt([
            ["card_replacement", "hist.cardReplacement"],
            ["new_card", "hist.newCard"],
          ]),
        },
        {
          name: "print_reason",
          label: t("hist.printReason"),
          type: "select",
          options: opt([
            ["damaged", "hist.damaged"],
            ["renew", "hist.renew"],
            ["new", "hist.new"],
            ["lost", "hist.lost"],
            ["others", "hist.others"],
            ["activate", "hist.activate"],
            ["upgrade", "hist.upgrade"],
            ["downgrade", "hist.downgrade"],
          ]),
        },
        {
          name: "print_status",
          label: t("hist.printStatus"),
          type: "select",
          options: opt([
            ["pending", "hist.pending"],
            ["printed", "hist.printed"],
            ["failed", "hist.failed"],
          ]),
        },
        { name: "date_printed", label: t("hist.datePrinted"), type: "date" },
        { name: "additional_reason", label: t("hist.additionalReason"), type: "textarea" },
        { name: "card_number", label: t("hist.cardNumber") },
      ],
    },
    {
      key: "transactions",
      model: "member_card_transaction",
      label: t("hist.transactionHistory"),
      path: "card-transactions",
      singular: t("hist.cardTransaction"),
      columns: [
        col("transaction_number", "hist.transactionNumber"),
        col("payment_purpose", "hist.paymentPurpose"),
        col("mode_of_payment", "hist.modeOfPayment"),
        col("amount", "hist.amount"),
        col("number_of_years", "hist.years"),
        col("transaction_date", "hist.transactionDate"),
        col("renew_club_id", "hist.renewClub"),
        col("is_transaction_reverted", "hist.reverted"),
      ],
      fields: [
        {
          name: "renew_club_id",
          label: t("hist.renewClub"),
          type: "fk",
          endpoint: "/clubs/compact",
        },
        { name: "mode_of_payment", label: t("hist.modeOfPayment") },
        { name: "payment_purpose", label: t("hist.paymentPurpose") },
        { name: "operator_number", label: t("hist.operatorNumber") },
        { name: "transaction_number", label: t("hist.transactionNumber") },
        { name: "register_number", label: t("hist.registerNumber") },
        {
          name: "number_of_years",
          label: t("hist.numberOfYears"),
          type: "number",
          step: "0.5",
          min: 0,
        },
        { name: "amount", label: t("hist.amount"), type: "number", step: "0.01", min: 0 },
        { name: "transaction_date", label: t("hist.transactionDate"), type: "date" },
        { name: "is_transaction_reverted", label: t("hist.isReverted"), type: "boolean" },
      ],
    },
    {
      key: "amendments",
      model: "member_amendment_detail",
      label: t("hist.amendmentHistory"),
      path: "amendment-details",
      singular: t("hist.amendmentDetail"),
      columns: [
        col("amendment_type", "hist.amendmentType"),
        col("reason", "hist.reason"),
        col("old_account_status", "hist.oldAccountStatus"),
        col("is_printed", "hist.printed"),
        col("created_at", "hist.date"),
        col("created_by_id", "hist.createdBy"),
      ],
      fields: [
        { name: "amendment_type", label: t("hist.amendmentType"), required: true },
        { name: "reason", label: t("hist.reason"), required: true },
        {
          name: "old_account_status",
          label: t("hist.oldAccountStatus"),
          type: "select",
          options: opt([
            ["active", "mem.active"],
            ["expired", "mem.expired"],
            ["cancelled", "mem.cancelled"],
            ["blacklisted", "mem.blacklisted"],
          ]),
        },
        { name: "is_printed", label: t("hist.isPrinted"), type: "boolean" },
      ],
    },
    {
      key: "supplementary",
      model: "member_supplementary_history",
      label: t("hist.primaryTable"),
      path: "supplementary-history",
      singular: t("hist.supplementaryHistory"),
      columns: [
        col("child_name", "hist.name"),
        col("child_card_number", "hist.cardNumber"),
        col("child_valid_till", "hist.validTill"),
        col("parent_name", "hist.parentName"),
        col("parent_card_number", "hist.parentCardNumber"),
        col("parent_valid_till", "hist.parentValidTill"),
      ],
      fields: [
        { name: "child_name", label: t("hist.name") },
        { name: "child_card_number", label: t("hist.cardNumber") },
        { name: "child_valid_till", label: t("hist.validTill"), type: "date" },
        { name: "parent_name", label: t("hist.parentName") },
        { name: "parent_card_number", label: t("hist.parentCardNumber") },
        { name: "parent_valid_till", label: t("hist.parentValidTill"), type: "date" },
      ],
    },
    {
      key: "attachments",
      model: "member_attachment",
      label: t("md.attachments"),
      path: "attachments",
      singular: t("hist.attachment"),
      columns: [
        col("title", "hist.title"),
        col("created_at", "hist.added"),
        col("created_by_id", "hist.addedBy"),
      ],
      fields: [
        {
          name: "title",
          label: t("hist.title"),
          type: "select",
          required: true,
          options: words(ATTACHMENT_TITLES),
        },
        { name: "file_url", label: t("hist.file"), type: "file", required: true },
      ],
    },
    {
      key: "notes",
      model: "member_note",
      label: t("hist.notes"),
      path: "notes",
      singular: t("hist.note"),
      columns: [
        col("title", "hist.title"),
        col("note", "hist.note"),
        col("left_at", "hist.leftAt"),
        col("created_at", "hist.added"),
        col("created_by_id", "hist.addedBy"),
      ],
      fields: [
        {
          name: "title",
          label: t("hist.title"),
          type: "select",
          required: true,
          options: words(NOTE_TITLES),
        },
        { name: "note", label: t("hist.note"), type: "textarea", required: true },
        {
          name: "reason_id",
          label: t("hist.reason"),
          type: "select",
          loadOptions: (_parent, signal) => reasonsOf("endorsement", signal),
        },
        { name: "club_id", label: t("hist.club"), type: "fk", endpoint: "/clubs/compact" },
        { name: "left_at", label: t("hist.leftAt"), type: "select", options: words(LEFT_AT) },
        {
          name: "promo_code_id",
          label: t("hist.promoCode"),
          type: "fk",
          endpoint: "/promo-codes/compact",
        },
        {
          name: "complaint_reason",
          label: t("hist.complaintReason"),
          type: "select",
          options: words(COMPLAINT_REASONS),
        },
        {
          name: "requested_by_id",
          label: t("hist.requestedBy"),
          type: "fk",
          endpoint: "/clubs/compact",
        },
        {
          name: "endorsed_by_id",
          label: t("hist.endorsedBy"),
          type: "select",
          loadOptions: (_parent, signal) => reasonsOf("complimentary_endorsed_by", signal),
        },
        {
          name: "approved_by_id",
          label: t("hist.approvedBy"),
          type: "select",
          loadOptions: (_parent, signal) => reasonsOf("complimentary_approved_by", signal),
        },
      ],
    },
  ];
};

/** An enum's values as choices, spelt the way the backend spells them, spaced. */
const words = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value.replace(/_/g, " ") }));

/** One of the configured reason lists, as choices. */
const reasonsOf = async (kind: string, signal?: AbortSignal) =>
  (
    await api.get<{ id: number; name: string }[]>("/reasons/compact", {
      params: { kind },
      ...(signal ? { signal } : {}),
    })
  ).data.map((r) => ({ value: String(r.id), label: r.name }));

/** What a note is about, as `app.core.enums.NoteTitle` spells it — one of each per member. */
const NOTE_TITLES = ["pre_opening", "endorsement", "complimentary"] as const;
const LEFT_AT = [
  "membership_counter",
  "front_end_counter",
  "food_service_counter",
  "selling_area",
  "parking_area",
  "outside_club",
] as const;
const COMPLAINT_REASONS = [
  "membership",
  "pending_request",
  "promo_snr",
  "service",
  "employee",
  "billing",
  "facility",
] as const;

const SIZES = [10, 25, 50];

/** What an attachment can be, as `app.core.enums.AttachmentTitle` spells it. */
const ATTACHMENT_TITLES = [
  "application_form",
  "cancellation_form",
  "documents",
  "id_card",
  "membership_information_update_form",
  "others",
] as const;

/**
 * What to call the file when someone saves it.
 *
 * There is no `file_name` column any more — the name is in the URL, and a
 * second copy of it is a second thing that can disagree. A data URL has no
 * name in it, so those fall back to what the document is.
 */
const downloadName = (url: string, title: unknown): string => {
  const path = url.startsWith("data:") ? "" : (url.split(/[?#]/)[0] ?? "");
  const last = path.slice(path.lastIndexOf("/") + 1);
  return last || String(title ?? "attachment").replace(/_/g, " ");
};

const toForm = (row: Row, fields: FieldDef[]): Record<string, string> =>
  Object.fromEntries(
    fields.map((f) => {
      const value = row[f.name];
      if (value === null || value === undefined) return [f.name, ""];
      if (f.type === "date" && typeof value === "string") return [f.name, value.slice(0, 10)];
      return [f.name, String(value)];
    }),
  );

/**
 * The Details block: tabs and the active table. Controlled from outside so the
 * page's own buttons (View attachments, Add Notes, an amendment) can land here.
 */
export function MemberHistories({
  memberId,
  active,
  onActive,
  add,
}: {
  memberId: number;
  active: HistoryKey;
  onActive: (key: HistoryKey) => void;
  /** Bump to open the add form on the active tab, with these values filled. */
  add?: { nonce: number; initial?: Record<string, string> | undefined } | undefined;
}) {
  const t = useTranslation();
  const SECTIONS = useMemo(() => sectionsOf(t), [t]);
  const section = SECTIONS.find((s) => s.key === active) ?? SECTIONS[0]!;
  return (
    <section className="mt-8">
      <h2 className="text-title-3 text-heading">{t("hist.details")}</h2>
      <div
        className="bg-surface-inset mt-3 inline-flex max-w-full gap-0.5 overflow-x-auto rounded-[10px] p-0.5"
        role="tablist"
      >
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={s.key === active}
            onClick={() => onActive(s.key)}
            className={cn(
              "text-subhead shrink-0 rounded-lg px-3 py-1.5 font-medium transition-colors duration-[var(--duration-fast)]",
              s.key === active
                ? "bg-surface-raised text-fg shadow-[var(--shadow-lift)]"
                : "text-fg hover:text-accent",
            )}
          >
            {s.label}
          </button>
        ))}
      </div>
      <HistoryTable
        key={`${section.key}-${add?.nonce ?? 0}`}
        memberId={memberId}
        section={section}
        add={add}
      />
    </section>
  );
}

function HistoryTable({
  memberId,
  section,
  add,
}: {
  memberId: number;
  section: Section;
  add?: { nonce: number; initial?: Record<string, string> | undefined } | undefined;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const endpoint = `/members/${memberId}/${section.path}`;
  const rows = useQuery({
    queryKey: [endpoint],
    queryFn: async ({ signal }) => (await api.get<Row[]>(endpoint, { signal })).data,
  });
  const users = useQuery({
    queryKey: ["/users/compact"],
    queryFn: ({ signal }) => userOptions(signal),
    staleTime: 300_000,
  });
  const clubs = useQuery({
    queryKey: ["/clubs/compact"],
    queryFn: ({ signal }) => clubOptions(signal),
    staleTime: 300_000,
  });
  const name = (options: { value: number; label: string }[] | undefined, id: unknown) =>
    options?.find((o) => o.value === Number(id))?.label ?? String(id);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [endpoint] });
  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete(`${endpoint}/${id}`, ifMatch(list.find((row) => row.id === id))),
    onSuccess: () => {
      toast.success(t("hist.deleted").replace("{singular}", section.singular));
      void invalidate();
    },
    onError: (error) =>
      toast.error(
        errorMessage(error, t("hist.deleteFailed").replace("{singular}", section.singular)),
      ),
  });
  const allow = useCan();
  const canAdd = allow(`add__${section.model}`);
  const canEdit = allow(`change__${section.model}`);
  const canDelete = allow(`delete__${section.model}`);
  // An outside "add" remounts this table (see the key above), so it can start open.
  const [editing, setEditing] = useState<"new" | Row | null>(
    canAdd && add?.nonce ? "new" : null,
  );
  const [initial, setInitial] = useState<Record<string, string>>(add?.initial ?? {});

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  // A history row is a record of a moment, so removing one is confirmed.
  const [deleting, setDeleting] = useState<number | null>(null);
  const list = rows.data ?? [];
  const pages = Math.max(1, Math.ceil(list.length / size));
  const slice = list.slice((page - 1) * size, page * size);

  const show = (key: string, value: unknown) => {
    if (value === null || value === undefined || value === "") return "—";
    if (key === "created_by_id" || key === "updated_by_id") return name(users.data, value);
    if (key === "renew_club_id") return name(clubs.data, value);
    if (/_at$/.test(key) && typeof value === "string")
      return format(new Date(value), "MM/dd/yyyy h:mm a");
    if (typeof value === "boolean") return value ? t("rp.yes") : t("rp.no");
    return String(value).replace(/_/g, " ");
  };

  return (
    <div className="bg-surface-raised rounded-card mt-3 shadow-[var(--shadow-lift)]">
      <div className="flex justify-end px-3 pt-3">
        {canAdd ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-accent"
            onClick={() => {
              setInitial({});
              setEditing("new");
            }}
          >
            <Plus aria-hidden /> {t("rp.addSingular").replace("{singular}", section.singular)}
          </Button>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="text-callout w-full">
          <thead className="bg-surface-sunken text-fg-muted text-subhead text-left font-medium">
            <tr>
              {section.columns.map((c) => (
                <th key={c.key} className="px-4 py-3 whitespace-nowrap">
                  {c.label}
                </th>
              ))}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-line divide-y">
            {slice.map((row) => (
              <tr key={row.id} className="hover:bg-surface-sunken transition-colors">
                {section.columns.map((c) => (
                  <td key={c.key} className="text-fg max-w-md truncate px-4 py-3">
                    {show(c.key, row[c.key])}
                  </td>
                ))}
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <span className="text-subhead inline-flex items-center gap-1">
                    {section.key === "attachments" && typeof row.file_url === "string" ? (
                      <a
                        href={row.file_url}
                        download={downloadName(row.file_url, row.title)}
                        className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                      >
                        {t("hist.download")}
                      </a>
                    ) : null}
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={() => {
                          setInitial(toForm(row, section.fields));
                          setEditing(row);
                        }}
                        className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                      >
                        {t("common.edit")}
                      </button>
                    ) : null}
                    {canDelete ? (
                      <button
                        type="button"
                        onClick={() => setDeleting(row.id)}
                        className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 font-medium transition-colors"
                      >
                        {t("common.delete")}
                      </button>
                    ) : null}
                  </span>
                </td>
              </tr>
            ))}
            {rows.isPending
              ? Array.from({ length: 3 }, (_, index) => (
                  <tr key={index}>
                    {section.columns.map((c) => (
                      <td key={c.key} className="px-4 py-3">
                        <Skeleton className="h-3.5 w-2/3" />
                      </td>
                    ))}
                    <td className="px-4 py-3" />
                  </tr>
                ))
              : null}
            {!rows.isPending && list.length === 0 ? (
              <tr>
                <td colSpan={section.columns.length + 1} className="p-0">
                  <EmptyState
                    title={t("hist.noneYet").replace("{section}", section.label.toLowerCase())}
                    description={t("hist.noneBody").replace(
                      "{section}",
                      section.label.toLowerCase(),
                    )}
                    action={
                      canAdd ? (
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setInitial({});
                            setEditing("new");
                          }}
                        >
                          {t("rp.addSingular").replace("{singular}", section.singular)}
                        </Button>
                      ) : undefined
                    }
                  />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <div className="hairline-t text-fg-muted text-subhead flex items-center justify-end gap-1.5 px-4 py-3">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          className="hover:bg-surface-inset hover:text-fg grid size-8 place-items-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-35"
        >
          <ChevronLeft className="size-4" aria-hidden />
          <span className="sr-only">{t("hist.previous")}</span>
        </button>
        <span className="bg-accent text-accent-fg grid size-8 place-items-center rounded-lg font-medium tabular-nums">
          {page}
        </span>
        <button
          type="button"
          disabled={page >= pages}
          onClick={() => setPage(page + 1)}
          className="hover:bg-surface-inset hover:text-fg grid size-8 place-items-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-35"
        >
          <ChevronRight className="size-4" aria-hidden />
          <span className="sr-only">{t("hist.next")}</span>
        </button>
        <Select
          aria-label={t("common.rowsPerPage")}
          value={size}
          onChange={(e) => {
            setSize(Number(e.target.value));
            setPage(1);
          }}
          fieldSize="sm"
          className="ml-2 w-auto"
        >
          {SIZES.map((n) => (
            <option key={n} value={n}>
              {t("hist.perPage").replace("{n}", String(n))}
            </option>
          ))}
        </Select>
      </div>
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) remove.mutate(deleting);
          setDeleting(null);
        }}
        loading={remove.isPending}
        title={t("rp.confirmDeleteTitle").replace("{singular}", section.singular.toLowerCase())}
        message={t("hist.confirmDeleteBody")}
      />

      {editing ? (
        <EditDialog
          singular={section.singular}
          endpoint={endpoint}
          fields={section.fields}
          initial={initial}
          id={editing === "new" ? null : editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      ) : null}
    </div>
  );
}
